import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactDom from "react-dom";
import { useTranslation } from "react-i18next";
import Card from "../Card";
import Button from "../../atoms/Button";
import PopupOverlay from "../../organisms/Background/PopupOverlay";
import CircularImageCropper from "../ImageCropper";
import { getCroppedImageFile } from "../../../utils/imageCrop";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import "./index.scss";

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);

const normalizeUploadErrorMessage = (t, error) => {
  if (error?.response?.status === 403) {
    return t("entityImageUpload.errors.forbidden");
  }
  if (error?.response?.status === 400) {
    return t("entityImageUpload.errors.invalidFile");
  }
  if (error?.response?.status === 413) {
    return t("entityImageUpload.errors.fileSize");
  }
  return t("entityImageUpload.errors.update");
};

export default function EntityImageUploadModal({
  isOpen,
  title,
  currentUrl,
  onClose,
  onUpload,
  onUploaded,
}) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cropResetKey, setCropResetKey] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const objectUrlRef = useRef(null);
  const fileInputRef = useRef(null);

  const resolvedCurrentUrl = useMemo(
    () => (currentUrl ? resolveMediaUrl(currentUrl) : null),
    [currentUrl]
  );
  const modalTitle = title || t("entityImageUpload.title");

  const revokeObjectUrlIfNeeded = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const resetSelectedImage = useCallback(() => {
    revokeObjectUrlIfNeeded();
    setSelectedFile(null);
    setPreviewUrl(resolvedCurrentUrl);
    setCroppedAreaPixels(null);
    setCropResetKey((previous) => previous + 1);
  }, [resolvedCurrentUrl, revokeObjectUrlIfNeeded]);

  useEffect(() => () => {
    revokeObjectUrlIfNeeded();
  }, [revokeObjectUrlIfNeeded]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetSelectedImage();
    setError(null);
  }, [isOpen, resetSelectedImage]);

  if (!isOpen) {
    return null;
  }

  const handleFileSelect = (event) => {
    const fileInput = event.target;
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!ALLOWED_MIME_TYPES.has((file.type || "").toLowerCase())) {
      resetSelectedImage();
      setError(t("entityImageUpload.errors.invalidType"));
      fileInput.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      resetSelectedImage();
      setError(t("entityImageUpload.errors.fileSize"));
      fileInput.value = "";
      return;
    }

    revokeObjectUrlIfNeeded();
    const objectUrl = URL.createObjectURL(file);
    objectUrlRef.current = objectUrl;
    setSelectedFile(file);
    setPreviewUrl(objectUrl);
    setError(null);
    setCroppedAreaPixels(null);
    setCropResetKey((previous) => previous + 1);
    fileInput.value = "";
  };

  const handleUpload = async () => {
    if (!selectedFile || !previewUrl) {
      setError(t("entityImageUpload.errors.selectPhoto"));
      return;
    }

    try {
      setIsSubmitting(true);
      const fileName = selectedFile.name || `entity-${Date.now()}`;
      const mimeType = selectedFile.type || "image/jpeg";
      const shouldApplyCrop = Boolean(croppedAreaPixels);
      const fileToUpload = shouldApplyCrop
        ? await getCroppedImageFile(previewUrl, croppedAreaPixels, fileName, { mimeType })
        : selectedFile;
      const response = await onUpload(fileToUpload);
      const nextUrl = response?.url || null;
      onUploaded?.(nextUrl, response);
      onClose?.();
    } catch (uploadError) {
      setError(normalizeUploadErrorMessage(t, uploadError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileButtonClick = () => {
    if (!isSubmitting) {
      fileInputRef.current?.click();
    }
  };

  return ReactDom.createPortal(
    <PopupOverlay
      onClose={onClose}
      closeOnEsc={!isSubmitting}
      closeOnOverlayClick={!isSubmitting}
      aria-label={modalTitle}
    >
      <Card
        className="entity-image-upload-card image-upload-modal-card"
        title={modalTitle}
        body={(
          <div className="entity-image-upload-content image-upload-modal-content">
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}
            <div className="entity-image-upload__preview image-upload-modal-preview">
              <CircularImageCropper
                image={previewUrl}
                disabled={isSubmitting}
                interactive={Boolean(selectedFile)}
                resetSignal={cropResetKey}
                onCropComplete={setCroppedAreaPixels}
                size={220}
              />
            </div>
            <div className="image-upload-modal-upload-section">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                onChange={handleFileSelect}
                className="image-upload-modal__input"
                disabled={isSubmitting}
              />
              <Button
                variant="primary"
                shape="pill"
                className="image-upload-modal__button image-upload-modal__button--upload"
                onClick={handleFileButtonClick}
                disabled={isSubmitting}
              >
                {t("entityImageUpload.uploadNew")}
              </Button>
            </div>
          </div>
        )}
        footer={(
          <div className="entity-image-upload-actions image-upload-modal-actions">
            <Button
              variant="secondary"
              shape="pill"
              className="image-upload-modal__button image-upload-modal__button--secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              shape="pill"
              className="image-upload-modal__button image-upload-modal__button--primary"
              onClick={handleUpload}
              disabled={isSubmitting || !selectedFile}
            >
              {isSubmitting ? t("entityImageUpload.actions.updating") : t("entityImageUpload.actions.update")}
            </Button>
          </div>
        )}
      />
    </PopupOverlay>,
    document.getElementById("portal")
  );
}

EntityImageUploadModal.propTypes = {
  isOpen: PropTypes.bool,
  title: PropTypes.string,
  currentUrl: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onUploaded: PropTypes.func,
};

EntityImageUploadModal.defaultProps = {
  isOpen: false,
  title: null,
  currentUrl: null,
  onUploaded: undefined,
};
